import { useEffect, useMemo, useState } from 'react'
import { useTiles, type GalleryKind } from '../store/tiles'
import { getAuthorFeed, type FeedItem } from '../api/client'
import { allImagesOf, isAdultPost } from '../lib/embedMedia'
import { TilePreview } from '../components/TilePreview'
import { ImageLightbox, type LightboxTile } from '../components/ImageLightbox'
import { formatAgo } from '../components/RelativeTime'

interface CollectedItem {
  uri: string
  text: string
  authorHandle: string
  indexedAt: string
  isAdult: boolean
  embed: FeedItem['post']['embed']
}

interface Tile {
  uri: string
  src: string
  alt?: string
  isVideo: boolean
  isText: boolean
  text?: string
  playlistUrl?: string
  fullsize?: string
  item: CollectedItem
}

export function Tiles() {
  const handles = useTiles((s) => s.handles)
  const addHandle = useTiles((s) => s.addHandle)
  const removeHandle = useTiles((s) => s.removeHandle)
  const clearHandles = useTiles((s) => s.clearHandles)
  const kinds = useTiles((s) => s.kinds)
  const toggleKind = useTiles((s) => s.toggleKind)
  const columns = useTiles((s) => s.columns)
  const setColumns = useTiles((s) => s.setColumns)
  const fsColumns = useTiles((s) => s.fsColumns)
  const setFsColumns = useTiles((s) => s.setFsColumns)
  const sfw = useTiles((s) => s.sfw)
  const setSfw = useTiles((s) => s.setSfw)
  const nsfw = useTiles((s) => s.nsfw)
  const setNsfw = useTiles((s) => s.setNsfw)

  const [draft, setDraft] = useState('')
  const [items, setItems] = useState<CollectedItem[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [fullscreen, setFullscreen] = useState(false)

  // Live ago counter — same 1s cadence as bskyhq.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // Refresh feed whenever the handle list changes.
  useEffect(() => {
    let cancelled = false
    async function pull() {
      if (handles.length === 0) {
        setItems([])
        return
      }
      setBusy(true)
      setErr(null)
      const out: CollectedItem[] = []
      for (const h of handles) {
        if (cancelled) return
        try {
          const feed = await getAuthorFeed(h, 30)
          for (const f of feed) {
            const p = f.post
            out.push({
              uri: p.uri,
              text: p.record?.text ?? '',
              authorHandle: p.author.handle,
              indexedAt: p.indexedAt,
              isAdult: isAdultPost({
                labels: p.labels,
                record: p.record as { text?: string },
                text: p.record?.text,
              }),
              embed: p.embed,
            })
          }
        } catch (e) {
          setErr(`@${h}: ${(e as Error).message}`)
        }
      }
      if (!cancelled) {
        out.sort((a, b) => Date.parse(b.indexedAt) - Date.parse(a.indexedAt))
        setItems(out)
        setBusy(false)
      }
    }
    void pull()
    return () => {
      cancelled = true
    }
  }, [handles])

  const tiles = useMemo<Tile[]>(() => {
    const wantTxt = kinds.includes('txt')
    const wantImg = kinds.includes('img')
    const wantVid = kinds.includes('vid')
    const out: Tile[] = []
    const seenSrc = new Set<string>()
    const seenText = new Set<string>()
    for (const it of items) {
      if (it.isAdult && !nsfw) continue
      if (!it.isAdult && !sfw) continue
      const media = allImagesOf(it.embed)
      if (media.length === 0) {
        if (!wantTxt) continue
        if (!it.text.trim()) continue
        if (seenText.has(it.uri)) continue
        seenText.add(it.uri)
        out.push({
          uri: it.uri,
          src: '',
          isVideo: false,
          isText: true,
          text: it.text,
          item: it,
        })
        continue
      }
      for (const m of media) {
        const isVid = m.isVideo === true
        if (isVid && !wantVid) continue
        if (!isVid && !wantImg) continue
        if (seenSrc.has(m.src)) continue
        seenSrc.add(m.src)
        out.push({
          uri: it.uri,
          src: m.src,
          alt: m.alt,
          isVideo: isVid,
          isText: false,
          text: it.text,
          playlistUrl: m.playlistUrl,
          fullsize: m.fullsize,
          item: it,
        })
      }
    }
    return out
  }, [items, kinds, sfw, nsfw])

  useEffect(() => {
    setLightboxIndex(null)
  }, [kinds, sfw, nsfw, handles])

  useEffect(() => {
    if (!fullscreen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && lightboxIndex == null) setFullscreen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullscreen, lightboxIndex])

  const activeGridCols = fullscreen ? fsColumns : columns

  function onAdd(e: React.FormEvent) {
    e.preventDefault()
    const v = draft.trim()
    if (!v) return
    // Accept comma / space separated batches too.
    for (const part of v.split(/[\s,]+/)) addHandle(part)
    setDraft('')
  }

  const kindBtn = (k: GalleryKind) => {
    const on = kinds.includes(k)
    const tone =
      k === 'txt'
        ? on
          ? 'border-sky-400/30 bg-sky-500/10 text-sky-100'
          : 'border-white/10 bg-white/5 text-ink-300 hover:bg-white/10'
        : k === 'img'
          ? on
            ? 'border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-100'
            : 'border-white/10 bg-white/5 text-ink-300 hover:bg-white/10'
          : on
            ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
            : 'border-white/10 bg-white/5 text-ink-300 hover:bg-white/10'
    return (
      <button
        key={k}
        type="button"
        onClick={() => toggleKind(k)}
        className={`rounded-full border px-2 py-0.5 ${tone}`}
        title={`show ${k === 'txt' ? 'text-only posts' : k === 'img' ? 'images' : 'videos / GIFs'}`}
      >
        {on ? '✓ ' : ''}
        {k}
      </button>
    )
  }

  return (
    <main className="w-full p-4">
      <h1 className="mb-2 text-xl font-bold text-white">▦ tiles</h1>
      <form onSubmit={onAdd} className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="add handle (e.g. jay.bsky.team) — comma or space separates batches"
          className="glass-input max-w-md"
        />
        <button type="submit" className="glass-button">
          + add
        </button>
        {handles.length > 0 ? (
          <button
            type="button"
            onClick={clearHandles}
            className="glass-button"
            title="remove all handles"
          >
            ✕ clear
          </button>
        ) : null}
        {busy ? <span className="text-xs text-ink-400">refreshing…</span> : null}
      </form>
      {handles.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {handles.map((h) => (
            <span
              key={h}
              className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-ink-100"
            >
              @{h}
              <button
                type="button"
                onClick={() => removeHandle(h)}
                className="text-ink-400 hover:text-rose-200"
                title="remove"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-ink-400">kinds</span>
        {(['txt', 'img', 'vid'] as const).map(kindBtn)}
        <span className="ml-2 text-ink-400">flags</span>
        <button
          type="button"
          onClick={() => setSfw(!sfw)}
          className={
            sfw
              ? 'rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-100'
              : 'rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-ink-300 hover:bg-white/10'
          }
        >
          {sfw ? '✓ sfw' : 'sfw'}
        </button>
        <button
          type="button"
          onClick={() => setNsfw(!nsfw)}
          className={
            nsfw
              ? 'rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 text-rose-100'
              : 'rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-ink-300 hover:bg-white/10'
          }
        >
          {nsfw ? '✓ nsfw' : 'nsfw'}
        </button>
        <span className="ml-2 text-ink-400">cols</span>
        <input
          type="range"
          min={2}
          max={12}
          step={1}
          value={columns}
          onChange={(e) => setColumns(parseInt(e.target.value, 10))}
          className="w-24 accent-[color:var(--accent-strong)]"
        />
        <span className="w-6 text-right tabular-nums text-white">{columns}</span>
        <button
          type="button"
          onClick={() => setFullscreen((v) => !v)}
          className={
            fullscreen
              ? 'rounded-md border border-[color:var(--accent-strong)] bg-white/10 px-2 py-0.5 text-white'
              : 'rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-ink-200 hover:bg-white/10'
          }
          title="expand gallery to full window (esc to close)"
        >
          ⤢ fullscreen
        </button>
        <span className="ml-2 text-ink-400">{tiles.length} tiles</span>
      </div>

      {err ? (
        <div className="mb-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
          {err}
        </div>
      ) : null}

      {tiles.length === 0 && !busy ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-ink-300">
          {handles.length === 0
            ? 'add a handle above to populate the gallery.'
            : 'nothing matched the current filters — flip a kind / sfw flag.'}
        </div>
      ) : (
        <div
          className={
            fullscreen
              ? 'fixed inset-0 z-50 flex flex-col overflow-auto bg-ink-950/95 p-3 backdrop-blur-sm'
              : ''
          }
        >
          {fullscreen ? (
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-ink-200">
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
                ▦ tiles · fullscreen
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
                {tiles.length} tiles · {activeGridCols} cols
              </span>
              <label className="ml-1 flex items-center gap-1 text-ink-300">
                cols
                <input
                  type="range"
                  min={3}
                  max={20}
                  step={1}
                  value={fsColumns}
                  onChange={(e) => setFsColumns(parseInt(e.target.value, 10))}
                  className="accent-[color:var(--accent-strong)]"
                />
                <span className="w-6 text-right tabular-nums text-white">{fsColumns}</span>
              </label>
              <button
                type="button"
                className="ml-auto rounded-full border border-white/15 bg-white/5 px-2 py-0.5 hover:bg-white/10"
                onClick={() => setFullscreen(false)}
              >
                ✕ close fullscreen
              </button>
            </div>
          ) : null}
          <div
            className="grid gap-1"
            style={{
              gridTemplateColumns: `repeat(${activeGridCols}, minmax(0, 1fr))`,
            }}
          >
            {tiles.map((t, idx) => {
              const textPx = Math.max(
                11,
                Math.min(18, Math.round(24 - activeGridCols * 0.8)),
              )
              const postedMs = Date.parse(t.item.indexedAt)
              const tileKey = t.isText ? `txt:${t.uri}` : t.src
              return (
                <button
                  key={tileKey}
                  type="button"
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative aspect-square overflow-hidden rounded-md border border-white/10 bg-white/5 hover:border-white/30"
                  title={`@${t.item.authorHandle}`}
                >
                  {t.isText ? (
                    <div className="flex h-full w-full flex-col gap-1 px-2 pb-7 pt-7 text-left">
                      <span className="text-[9px] uppercase tracking-wide text-ink-400">
                        ✎ text · @{t.item.authorHandle}
                      </span>
                      <p
                        className="line-clamp-[10] overflow-hidden whitespace-pre-wrap break-words text-ink-100"
                        style={{ fontSize: `${textPx}px`, lineHeight: 1.35 }}
                      >
                        {t.text}
                      </p>
                    </div>
                  ) : (
                    <TilePreview
                      src={t.src}
                      fullsize={t.fullsize}
                      isVideo={t.isVideo}
                      playlistUrl={t.playlistUrl}
                    />
                  )}
                  <span className="absolute left-1 top-1 rounded-full border border-white/10 bg-ink-900/70 px-1.5 py-0.5 text-[9px] text-white">
                    @{t.item.authorHandle}
                  </span>
                  {t.item.isAdult ? (
                    <span className="absolute right-1 top-1 rounded-full border border-rose-400/30 bg-rose-500/40 px-1.5 py-0.5 text-[9px] font-bold text-white">
                      18+
                    </span>
                  ) : null}
                  {Number.isFinite(postedMs) ? (
                    <span
                      className="absolute bottom-1 right-1 rounded-full border border-white/10 bg-ink-900/70 px-1.5 py-0.5 text-[9px] text-ink-100"
                      title={`posted ${new Date(postedMs).toLocaleString()}`}
                    >
                      🕒 {formatAgo(postedMs, now, true)}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
          {lightboxIndex != null ? (
            <ImageLightbox
              tiles={tiles.map<LightboxTile>((t) => ({
                postUri: t.uri,
                src: t.src,
                alt: t.alt,
                isVideo: t.isVideo,
                isText: t.isText,
                text: t.text,
                playlistUrl: t.playlistUrl,
                fullsize: t.fullsize,
                source: t.isText ? 'text' : t.isVideo ? 'video' : 'image',
                isAdult: t.item.isAdult,
                authorHandle: t.item.authorHandle,
                whenIso: t.item.indexedAt,
              }))}
              index={lightboxIndex}
              onIndex={setLightboxIndex}
              onClose={() => setLightboxIndex(null)}
              blurAdult={!nsfw}
            />
          ) : null}
        </div>
      )}
    </main>
  )
}
